// Maps a merged Branding onto the runtime CSS variables for a brand-scoped
// container (app shell, login card, public report). Only the BASE variables
// are set inline — the derived variants (--sn-accent-light / --sn-accent-tint /
// --sn-accent-tint-strong) are defined once in global.css as color-mix() over
// var(--sn-accent), so they cascade to the active brand automatically.

import type { CSSProperties } from "react";
import type { Branding } from "../config/types";

/**
 * Inline style carrying the active brand's accent variables, or undefined when
 * the brand doesn't override the Side Nerd defaults. The accent and its ink are
 * always set together: a brand that supplies `accent` without `accentInk` gets
 * a derived darkening rather than inheriting another brand's ink, so a product
 * can never render one accent with a different family's text/tints/focus rings.
 * `onAccent` falls back to the global.css default (dark, correct for the
 * default aqua); brands whose accent needs white text must supply it (the
 * product registry always does — see BRAND.md).
 */
export function brandVars(branding: Branding): CSSProperties | undefined {
  if (!branding.accent) return undefined;
  const vars: Record<string, string> = {
    "--sn-accent": branding.accent,
    "--sn-accent-ink":
      branding.accentInk ?? `color-mix(in srgb, ${branding.accent} 60%, black)`,
  };
  if (branding.onAccent) vars["--sn-on-accent"] = branding.onAccent;
  return vars as CSSProperties;
}
